import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserService } from '@/services/user/userService';
import { UpdateUserData, UpdateProfilePictureData, UpdatePasswordData } from '@/types/userDetails';
import { useContext } from 'react';
import { AuthContext } from '@/context/AuthContext';

export const useUser = () => {
  const queryClient = useQueryClient();
  const authContext = useContext(AuthContext);

  if (!authContext) {
    throw new Error('useUser must be used within an AuthProvider');
  }

  const { user, login: contextLogin } = authContext;

  // Get user details query - uses admin endpoint if user is admin
  const userDetailsQuery = useQuery({
    queryKey: ['userDetails', user?.type],
    queryFn: () => UserService.getUserDetails(user?.type),
    enabled: !!user,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  // Update user information mutation
  const updateUserMutation = useMutation({
    mutationFn: (userData: UpdateUserData) => UserService.updateUserInfo(userData, user?.type),
    onSuccess: (updatedUser) => {
      // Update auth context with new user data
      user.email = updatedUser.email;
      user.firstName = updatedUser.firstName;
      user.lastName = updatedUser.lastName;
      user.phone = updatedUser.phoneNumber ?? undefined;
      contextLogin(user);
      
      // Invalidate and refetch user details
      queryClient.invalidateQueries({ queryKey: ['userDetails'] });
    },
  });

  // Update profile picture mutation
  const updateProfilePictureMutation = useMutation({
    mutationFn: (fileData: UpdateProfilePictureData) => UserService.updateProfilePicture(fileData, user?.type),
    onSuccess: (response) => {
      // Invalidate and refetch user details and profile picture
      queryClient.invalidateQueries({ queryKey: ['userDetails'] });
      queryClient.invalidateQueries({ queryKey: ['profilePicture'] });
    },
  });

  // Update password mutation
  const updatePasswordMutation = useMutation({
    mutationFn: (passwordData: UpdatePasswordData) => UserService.updatePassword(passwordData, user?.type),
  });

  // Remove profile picture mutation
  const removeProfilePictureMutation = useMutation({
    mutationFn: () => UserService.removeProfilePicture(user?.type),
    onSuccess: () => {
      // Invalidate and refetch user details and profile picture
      queryClient.invalidateQueries({ queryKey: ['userDetails'] });
      queryClient.invalidateQueries({ queryKey: ['profilePicture'] });
    },
  });

  // Update showQuickGuide mutation
  const updateShowQuickGuideMutation = useMutation({
    mutationFn: (showQuickGuide: boolean) => UserService.updateShowQuickGuide(showQuickGuide),
    onSuccess: (updatedUser) => {
      // Invalidate and refetch user details
      queryClient.invalidateQueries({ queryKey: ['userDetails'] });
    },
  });

  return {
    // User details
    userDetails: userDetailsQuery.data,
    isLoadingUserDetails: userDetailsQuery.isLoading,
    userDetailsError: userDetailsQuery.error,
    
    // Update user information
    updateUser: updateUserMutation.mutate,
    updateUserAsync: updateUserMutation.mutateAsync,
    isUpdatingUser: updateUserMutation.isPending,
    updateUserError: updateUserMutation.error,
    updateUserSuccess: updateUserMutation.isSuccess,
    
    // Update profile picture
    updateProfilePicture: updateProfilePictureMutation.mutateAsync,
    updateProfilePictureAsync: updateProfilePictureMutation.mutateAsync,
    isUpdatingProfilePicture: updateProfilePictureMutation.isPending,
    updateProfilePictureError: updateProfilePictureMutation.error,
    updateProfilePictureSuccess: updateProfilePictureMutation.isSuccess,
    
    // Update password
    updatePassword: updatePasswordMutation.mutate,
    updatePasswordAsync: updatePasswordMutation.mutateAsync,
    isUpdatingPassword: updatePasswordMutation.isPending,
    updatePasswordError: updatePasswordMutation.error,
    updatePasswordSuccess: updatePasswordMutation.isSuccess,
    
    // Remove profile picture
    removeProfilePicture: removeProfilePictureMutation.mutateAsync,
    removeProfilePictureAsync: removeProfilePictureMutation.mutateAsync,
    isRemovingProfilePicture: removeProfilePictureMutation.isPending,
    removeProfilePictureError: removeProfilePictureMutation.error,
    removeProfilePictureSuccess: removeProfilePictureMutation.isSuccess,
    
    // Update showQuickGuide
    updateShowQuickGuide: updateShowQuickGuideMutation.mutate,
    updateShowQuickGuideAsync: updateShowQuickGuideMutation.mutateAsync,
    updateShowQuickGuideMutation,
    isUpdatingShowQuickGuide: updateShowQuickGuideMutation.isPending,
    updateShowQuickGuideError: updateShowQuickGuideMutation.error,
    updateShowQuickGuideSuccess: updateShowQuickGuideMutation.isSuccess,
    
    // Reset functions
    resetUpdateUser: updateUserMutation.reset,
    resetUpdateProfilePicture: updateProfilePictureMutation.reset,
    resetUpdatePassword: updatePasswordMutation.reset,
    resetRemoveProfilePicture: removeProfilePictureMutation.reset,
    resetUpdateShowQuickGuide: updateShowQuickGuideMutation.reset,
  };
};
